import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { first, isEmpty, isNil } from 'lodash';
import classNames from 'classnames';
// Import Store
import { useAppDispatch } from 'store';
import { changeTemplateValue } from 'store/slices/template';
// Import UI Components
import { Dropdown, ExtendedInput, Icon } from '@geneui/components';
// Import Components
import UploadFile from 'components/common/UploadFile';
import ButtonWithLoader from 'components/common/ButtonWithLoader';
import DynamicVariables from '../DynamicVariables';
import TemplateCategoryInput from '../TemplateCategoryInput';
import ButtonRowsBuilder from './ButtonRowsBuilder';
import BlockedBySubscriptionInfo from 'components/common/BlockedBySubscriptionInfo';
// Import Helpers
import { calculateTemplateBodyLengthWithRegEx } from 'services/Helpers';
// Import Services
import { TemplateSymbolsCountFields, getTemplateValidationTranslatableErrorText } from 'services/template';
import { TemplateHttpService } from 'services/http';
// Import Hooks
import useBind from 'hooks/UseBind';
import useRequest from 'hooks/UseRequest';
import useToaster from 'hooks/UseToaster';
import useFieldValidity from 'hooks/UseFieldValidity';
import useInputInsertion from 'hooks/UseInputInsertion';
import useImageURLPlaceholder from 'hooks/UseImageURLPlaceholder';
import { useValidator } from 'hooks'; //TODO: change to direct import later
// Import Enums
import { TemplateTypeEnum } from 'enums';
// Import Constants
import { AlertTypes, BLOCKED_BY_SUBSCRIPTION_CLASSNAME, l, TemplateInputTypes } from 'constants/common';
import { DefaultLanguage } from 'constants/template';
import { TELEGRAM_MAX_UPLOAD_SIZE_IN_BYTES, TELEGRAM_MAX_UPLOAD_SIZE_IN_MEGABYTES } from './constants';
// Import Styles
import 'assets/scss/socialNotification.scss';
import styles from './telegram.module.scss';

const TelegramTemplateContent = ({
    templateData,
    templateLangKey,
    handleChange,
    shortenerAndTrackerSettings,
    dynamicVariables,
}) => {
    const { t } = useTranslation();
    const { showToaster } = useToaster();
    const { doPostRequest } = useRequest();
    const dispatch = useAppDispatch();

    const { insertionHandler, updateInsertionHandler, insertionOptions, setInsertionOptions } = useInputInsertion();
    const templateBody = templateData?.[TemplateInputTypes.Body];

    const bodyValidationState = useFieldValidity(TemplateInputTypes.Body, l.Body, templateLangKey);
    const imageURLPlaceholderDropdownValidationState = useFieldValidity(
        TemplateInputTypes.ImageURLPlaceholderDropdown,
        l.ImageURLPlaceholderDropdown,
        templateLangKey,
    );
    const handleCategoryChange = useBind(handleChange, TemplateInputTypes.Category);

    const validator = useValidator(getTemplateValidationTranslatableErrorText(t));

    const [isUploadedImageLoading, setIsUploadedImageLoading] = useState(false);
    const [isUploadBtnDisabled, setIsUploadBtnDisabled] = useState(true);
    const [file, setFile] = useState<Nullable<File>>(null);
    const [bodyValue, setBodyValue] = useState(templateBody);
    const [moveCursorToPosition, setMoveCursorToPosition] = useState<number | null>(null);

    const inputRef = useRef<HTMLInputElement>(null);

    const categoryValue = templateData?.[TemplateInputTypes.Category];
    const computedBody = useMemo(() => {
        if (isEmpty(shortenerAndTrackerSettings) || isEmpty(templateBody)) return '';
        return calculateTemplateBodyLengthWithRegEx(shortenerAndTrackerSettings, templateBody);
    }, [templateBody, shortenerAndTrackerSettings]);

    // kept out of the hook: an asset upload must not be aborted when the user switches tabs or leaves the page
    const { addAssetRequest } = useMemo(
        () => ({
            // eslint-disable-next-line no-restricted-syntax -- upload must survive unmount
            addAssetRequest: TemplateHttpService.addAsset(),
        }),
        [],
    );

    const {
        handleImageURLPlaceholderDropdownChange,
        imagePreviewURL,
        imageURLPlaceholderDropdownData,
        isBlockedImageURLPlaceholderDropdown,
        isDisabledUploadFileBtn,
        isDisabledImageURLPlaceholderDropdown,
    } = useImageURLPlaceholder({
        templateData,
        templateLangKey,
        templateType: TemplateTypeEnum.Telegram,
        dynamicVariables,
    });

    const fileInputChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const fileInput = e.currentTarget;
        setFile(first(fileInput.files) ?? null);
        dispatch(
            changeTemplateValue({
                propertyName: TemplateInputTypes.FileName,
                value: !fileInput.value ? null : first(fileInput.files)?.name,
                templateLangKey,
            }),
        );
        dispatch(
            changeTemplateValue({
                propertyName: TemplateInputTypes.UploadedImageUrl,
                value: '',
                templateLangKey,
            }),
        );
        setIsUploadBtnDisabled(!fileInput.value);
    };

    const uploadImgHandler = () => {
        if (isNil(file)) return;

        if (file.size > TELEGRAM_MAX_UPLOAD_SIZE_IN_BYTES) {
            showToaster(
                AlertTypes.error,
                t(l.TelegramMaxUploadSizeError, { maxUploadSizeInMegabytes: TELEGRAM_MAX_UPLOAD_SIZE_IN_MEGABYTES }),
            );

            return;
        }

        setIsUploadedImageLoading(true);
        const formData = new FormData();
        formData.append('file', file);

        doPostRequest(addAssetRequest.request, {
            requestBody: formData,
            successCallback: (Data) => {
                dispatch(
                    changeTemplateValue({
                        propertyName: TemplateInputTypes.UploadedImageUrl,
                        value: first(Data)?.Url,
                        templateLangKey,
                    }),
                );
                setIsUploadedImageLoading(false);
                setIsUploadBtnDisabled(true);
            },
            errorCallback: () => {
                setIsUploadedImageLoading(false);
                setIsUploadBtnDisabled(false);
            },
        });
    };

    const updateDynamicVariableOptions = useCallback(
        ({ hintText, focusedInputKey }) => {
            setInsertionOptions({ channel: TemplateTypeEnum.Telegram, inputFilterKey: '', hintText, focusedInputKey });
        },
        [setInsertionOptions],
    );

    const inlineKeyboardChangeHandler = useCallback(
        (values, isValid) => {
            dispatch(
                changeTemplateValue({
                    propertyName: TemplateInputTypes.InlineKeyboard,
                    value: values,
                    templateLangKey,
                }),
            );
            dispatch(
                changeTemplateValue({
                    propertyName: TemplateInputTypes.InlineKeyboardFormikValidity,
                    value: isValid,
                    templateLangKey,
                }),
            );
            validator({ [templateLangKey + TemplateInputTypes.InlineKeyboardFormikValidity]: isValid });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatch, templateLangKey],
    );

    const deleteUploadedImageHandler = () => {
        dispatch(
            changeTemplateValue({
                propertyName: TemplateInputTypes.UploadedImageUrl,
                value: '',
                templateLangKey,
            }),
        );
        dispatch(
            changeTemplateValue({
                propertyName: TemplateInputTypes.FileName,
                value: '',
                templateLangKey,
            }),
        );
        setIsUploadBtnDisabled(true);
        setFile(null);
    };

    const onChange = (e) => {
        const value = e.target.value;

        setBodyValue(value);
        handleChange(TemplateInputTypes.Body, value);
    };

    const onBodyFocus = useCallback(() => {
        updateDynamicVariableOptions({ hintText: '', focusedInputKey: TemplateInputTypes.Body });
    }, [updateDynamicVariableOptions]);

    const onButtonFocus = () => {
        updateDynamicVariableOptions({
            hintText: l.DynamicVariablesAreNotSupported,
            focusedInputKey: TemplateInputTypes.InlineKeyboard,
        });
    };

    const insertTextAtCursor = (dynamicVariable) => {
        const textToInsert = dynamicVariable.FieldName;
        const selectionStart = inputRef.current?.selectionStart;
        const firstPart = bodyValue.slice(0, selectionStart);
        const secondPart = bodyValue.slice(selectionStart, bodyValue.length);
        const insertedText = ` {${textToInsert}} `;
        const resultValue = `${firstPart}${insertedText}${secondPart}`;

        setBodyValue(resultValue);
        setMoveCursorToPosition(firstPart.length + insertedText.length);
        handleChange(TemplateInputTypes.Body, resultValue);
    };

    updateInsertionHandler(TemplateInputTypes.Body, insertTextAtCursor);

    // The caret is placed after the textarea receives the new value; setting it earlier is lost
    // because writing the controlled value moves the caret to the end.
    useEffect(() => {
        if (!isNil(moveCursorToPosition)) {
            inputRef.current?.focus();
            inputRef.current?.setSelectionRange(moveCursorToPosition, moveCursorToPosition);
            setMoveCursorToPosition(null);
        }
    }, [moveCursorToPosition]);

    useEffect(() => {
        updateDynamicVariableOptions({ hintText: '', focusedInputKey: TemplateInputTypes.Body });
    }, [updateDynamicVariableOptions]);

    return (
        <>
            <DynamicVariables
                dynamicVariables={dynamicVariables}
                clickHandler={insertionHandler}
                insertionOptions={insertionOptions}
            />
            <div className="crm-create-template-page-c-c-h-inputs-row">
                {templateLangKey === DefaultLanguage && (
                    <TemplateCategoryInput value={categoryValue} onChange={handleCategoryChange} />
                )}
            </div>
            <div className={styles.mainContainer}>
                <div className={styles.leftSection}>
                    <div className="crm-create-template-page-c-c-content">
                        <div className="crm-messaging-row">
                            <div className="crm-messaging-row-head">
                                <p>{t(l.SymbolsCountIn, { pageName: TemplateTypeEnum.Telegram })}</p>
                                <span>{`(${computedBody.length} ${t(l.Bytes)} / ${
                                    TemplateSymbolsCountFields[TemplateTypeEnum.Telegram]
                                } ${t(l.Bytes)})`}</span>
                            </div>
                            <ExtendedInput
                                ref={inputRef}
                                type="textarea"
                                value={bodyValue}
                                colorBorderOnError={true}
                                isValid={bodyValidationState.isValid}
                                errorText={bodyValidationState.errorText}
                                placeholder={t(l.TypeYourText)}
                                onChange={onChange}
                                onFocus={onBodyFocus}
                                // eslint-disable-next-line jsx-a11y/no-autofocus
                                autoFocus={true}
                            />
                        </div>
                    </div>
                    <div className="social-template-section-container">
                        <div className="social-n-details-panel">
                            <div className="social-n-details-panel-content">
                                <div>
                                    <BlockedBySubscriptionInfo
                                        isBlocked={isBlockedImageURLPlaceholderDropdown}
                                        position="right"
                                    >
                                        <div
                                            className={classNames('crm-image-url-placeholder-dropdown', {
                                                [`${BLOCKED_BY_SUBSCRIPTION_CLASSNAME} not-clickable`]:
                                                    isBlockedImageURLPlaceholderDropdown,
                                            })}
                                        >
                                            <Dropdown
                                                clearable={true}
                                                data={imageURLPlaceholderDropdownData}
                                                value={templateData?.UploadedImageUrl}
                                                onChange={handleImageURLPlaceholderDropdownChange}
                                                disabled={isDisabledImageURLPlaceholderDropdown}
                                                placeholder={t(l.ImageURLPlaceholderDropdown)}
                                                isValid={imageURLPlaceholderDropdownValidationState.isValid}
                                                errorText={imageURLPlaceholderDropdownValidationState.errorText}
                                            />
                                        </div>
                                    </BlockedBySubscriptionInfo>
                                    <div className="social-n-details-panel-choose-file">
                                        <p className="social-n-details-panel-choose-file-title"> {t(l.Media)}:</p>
                                        <div className="social-n-details-panel-browse">
                                            <UploadFile
                                                value={templateData?.FileName}
                                                onChange={fileInputChangeHandler}
                                                acceptedExtensions=".svg,.png,.jpg"
                                                disabled={isDisabledUploadFileBtn}
                                            />
                                        </div>
                                        <div>
                                            <ButtonWithLoader
                                                onClick={uploadImgHandler}
                                                disabled={
                                                    isUploadBtnDisabled ||
                                                    isDisabledUploadFileBtn ||
                                                    isUploadedImageLoading
                                                }
                                                isLoading={isUploadedImageLoading}
                                            >
                                                {t(l.Upload)}
                                            </ButtonWithLoader>
                                        </div>

                                        <div>
                                            <div className="social-c-file-upload-details">
                                                <p>{t(l.MediaPreview)}:</p>
                                                <div className="social-c-file-upload-details-img-wrapper">
                                                    {imagePreviewURL && <img alt="noImage" src={imagePreviewURL} />}
                                                </div>
                                                <Icon
                                                    type="bc-icon-trash"
                                                    disabled={!imagePreviewURL}
                                                    onClick={deleteUploadedImageHandler}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <p>{t(l.ImageUploadRequirements)}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.rightSection}>
                    <ButtonRowsBuilder
                        onChange={inlineKeyboardChangeHandler}
                        initialData={templateData?.[TemplateInputTypes.InlineKeyboard] ?? [[]]}
                        onFocus={onButtonFocus}
                    />
                </div>
            </div>
        </>
    );
};

TelegramTemplateContent.propTypes = {
    templateData: PropTypes.object.isRequired,
    templateLangKey: PropTypes.string.isRequired,
    handleChange: PropTypes.func.isRequired,
    shortenerAndTrackerSettings: PropTypes.array.isRequired,
    dynamicVariables: PropTypes.arrayOf(PropTypes.object),
};

TelegramTemplateContent.defaultProps = {
    focusOnMount: true,
};

export default TelegramTemplateContent;
